/**
 * Drop-in Shim für `@google/generative-ai` (das alte Gemini-SDK).
 *
 * Seit 2026-04-24 läuft AI über die Cloud Function `aiGateway` statt direkter Gemini-Calls.
 * Dieser Shim stellt die gleiche API-Oberfläche wie `GoogleGenerativeAI` bereit,
 * delegiert intern aber an die Cloud Function.
 */

import { httpsCallable, getFunctions } from '@firebase/functions';
import { getApp } from '@firebase/app';

const APP_ID = 'goodplace-match';

interface GatewayResponse {
    text: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    candidates?: any[];
    usageMetadata?: unknown;
    finishReason?: string;
}

type GatewayCallable = ReturnType<typeof httpsCallable<
    { app: string; model: string; contents: unknown; config?: Record<string, unknown> },
    GatewayResponse
>>;

let cachedCallable: GatewayCallable | null = null;
function getCallable(): GatewayCallable {
    if (!cachedCallable) {
        const functions = getFunctions(getApp(), 'europe-west3');
        cachedCallable = httpsCallable(functions, 'aiGateway');
    }
    return cachedCallable;
}

function normalizeContent(content: unknown): unknown {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
        return content.map((item: unknown) =>
            typeof item === 'string' ? { text: item } : item,
        );
    }
    return content;
}

interface ModelConfig {
    model: string;
    systemInstruction?: string;
    generationConfig?: Record<string, unknown>;
    safetySettings?: unknown[];
    tools?: unknown[];
}

export type ChatSession = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sendMessage: (message: string) => Promise<{ response: { text: () => string; candidates?: any[] } }>;
};

class GenerativeModel {
    constructor(private config: ModelConfig) {}

    async generateContent(prompt: unknown) {
        const callable = getCallable();
        const mergedConfig: Record<string, unknown> = {
            ...(this.config.generationConfig || {}),
            ...(this.config.systemInstruction
                ? { systemInstruction: this.config.systemInstruction }
                : {}),
            ...(this.config.safetySettings ? { safetySettings: this.config.safetySettings } : {}),
            ...(this.config.tools ? { tools: this.config.tools } : {}),
        };
        const result = await callable({
            app: APP_ID,
            model: this.config.model,
            contents: normalizeContent(prompt),
            config: mergedConfig,
        });
        const data = result.data;
        return {
            response: {
                text: () => data.text || '',
                candidates: data.candidates,
            },
        };
    }

    startChat(opts: { history?: Array<{ role: string; parts: Array<{ text: string }> }> } = {}): ChatSession {
        const history = opts.history || [];
        return {
            sendMessage: async (message: string) => {
                const contents = [
                    ...history,
                    { role: 'user', parts: [{ text: message }] },
                ];
                const callable = getCallable();
                const mergedConfig: Record<string, unknown> = {
                    ...(this.config.generationConfig || {}),
                    ...(this.config.systemInstruction
                        ? { systemInstruction: this.config.systemInstruction }
                        : {}),
                };
                const result = await callable({
                    app: APP_ID,
                    model: this.config.model,
                    contents,
                    config: mergedConfig,
                });
                const data = result.data;
                return {
                    response: {
                        text: () => data.text || '',
                        candidates: data.candidates,
                    },
                };
            },
        };
    }
}

export class GoogleGenerativeAI {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    constructor(private _apiKey: string) {}

    getGenerativeModel(config: ModelConfig): GenerativeModel {
        return new GenerativeModel(config);
    }
}
